// The chat of the text channel shown, as Discord's: its messages — the newest ones, older ones asked for
// as the view comes near the top — one writer's next ones under the first of them, a line where another
// day begins; who types; and the box to write in (Composer.tsx). A message: its writer's picture, name
// and time; what it answers, a click away; its text (the chat's Markdown, the user lit where named), its
// images (a click shows one whole) and the reactions to it. Under the pointer — on a phone, once touched —
// its acts: a reaction, an answer, an edit and a deletion where the user may. Images dropped on the chat go
// into the box. The view follows the newest message while it is at the bottom, stays where it is while
// the user reads older ones, and offers a way back to the newest.
import type { ComponentChildren } from "preact";
import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import {
  answer,
  attach,
  boxed,
  editing,
  jumpTo,
  mayDelete,
  older,
  quotesDeleted,
  react,
  remove,
  saveEdit,
  type Quote,
} from "../chat.ts";
import { mentions } from "../markdown.ts";
import {
  changed,
  channelName,
  channelOf,
  fileUrl,
  isModerator,
  nameOf,
  state,
  type FileRef,
  type Message,
  type Pending,
} from "../store.ts";
import { Avatar } from "./Avatar.tsx";
import { Composer } from "./Composer.tsx";
import { EmojiPicker } from "./EmojiPicker.tsx";
import { Icon, IconButton } from "./Icon.tsx";
import { Rich, RichLine } from "./Markdown.tsx";

/** How near the top the view comes before older messages are asked for, how near the bottom it is still
 * at the bottom, and how far from it the way back to the newest shows, px. */
const NEAR_TOP = 200;
const NEAR_BOTTOM = 60;
const FAR = 400;
/** A writer's next message goes under the first while it comes this soon after the one before, ms. */
const TOGETHER = 7 * 60 * 1000;
/** Reactions a message's acts offer at once. */
const QUICK = ["👍", "❤️", "😂"];
/** The largest an image of a message shows alone, px. */
const IMAGE_W = 400;
const IMAGE_H = 320;

export function Chat() {
  const box = useRef<HTMLDivElement>(null);
  /** Where the view was at the last drawing: its distance from the bottom, and the oldest message. */
  const fromBottom = useRef(0);
  const oldest = useRef(Infinity);
  const [far, setFar] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [flash, setFlash] = useState(0);
  const channel = state.view.channel;
  const seqs = [...state.messages.keys()].sort((a, b) => a - b);
  const pending = [...state.pending.entries()].filter(([_, p]) => p.channel === channel);
  useLayoutEffect(() => {
    const b = box.current!;
    const first = seqs.length > 0 ? seqs[0] : Infinity;
    if (state.jump !== 0) {
      // The message an answer quotes, in the middle, lit for a moment.
      const el = b.querySelector(`[data-seq="${state.jump}"]`);
      if (el instanceof HTMLElement) {
        b.scrollTop = el.offsetTop - b.clientHeight / 2 + el.offsetHeight / 2;
        const seq = state.jump;
        setFlash(seq);
        setTimeout(() => setFlash((f) => (f === seq ? 0 : f)), 1600);
      }
      state.jump = 0;
    } else if (state.toEnd || fromBottom.current - b.clientHeight < NEAR_BOTTOM) {
      // At the bottom, or the user wrote: the newest message in view.
      b.scrollTop = b.scrollHeight;
      state.toEnd = false;
    } else if (first < oldest.current) {
      // Older messages came above: what the user reads stays where it was.
      b.scrollTop = b.scrollHeight - fromBottom.current;
    }
    oldest.current = first;
    fromBottom.current = b.scrollHeight - b.scrollTop;
  });
  // A page with room for more: older ones at once.
  useEffect(() => {
    const b = box.current!;
    if (b.scrollHeight <= b.clientHeight + NEAR_TOP) older();
  });
  const scrolled = () => {
    const b = box.current!;
    fromBottom.current = b.scrollHeight - b.scrollTop;
    if (b.scrollTop <= NEAR_TOP) older();
    const isFar = fromBottom.current - b.clientHeight > FAR;
    if (isFar !== far) setFar(isFar);
  };
  const toNewest = () => {
    const b = box.current!;
    b.scrollTop = b.scrollHeight;
  };
  const drop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const files = [...(e.dataTransfer?.files ?? [])].filter((f) => f.type.startsWith("image/"));
    if (files.length > 0) attach(files);
  };
  const c = channelOf(channel, "text");
  const rows: ComponentChildren[] = [];
  let before: Message | null = null;
  for (const s of seqs) {
    const m = state.messages.get(s)!;
    if ((before === null || dayOf(before.at) !== dayOf(m.at)) && m.at > 0) rows.push(<DayLine key={`d${s}`} at={m.at} />);
    rows.push(<MessageView key={s} m={m} head={before === null || !together(before, m)} flash={flash === s} />);
    before = m;
  }
  return (
    <div
      class={dragging ? "chat dragging" : "chat"}
      onDragOver={(e) => {
        if ([...(e.dataTransfer?.types ?? [])].includes("Files")) {
          e.preventDefault();
          setDragging(true);
        }
      }}
      onDragLeave={(e) => {
        if (e.currentTarget === e.target) setDragging(false);
      }}
      onDrop={drop}
    >
      <div class="messages" ref={box} onScroll={scrolled}>
        {state.all || (seqs.length === 0 && pending.length === 0) ? (
          <div class="channel-start">
            <span class="channel-start-icon">
              <Icon name="hash" size={36} />
            </span>
            <h2>Это начало канала #{c === null ? "" : channelName(c)}</h2>
            <p>Пишите, отвечайте, прикрепляйте изображения и ставьте реакции.</p>
          </div>
        ) : (
          state.asking && <div class="loading">загружаю…</div>
        )}
        {rows}
        {pending.map(([nonce, p]) => (
          <PendingView key={`p${nonce}`} p={p} />
        ))}
      </div>
      {far && (
        <button class="to-newest" onClick={toNewest}>
          <Icon name="arrowDown" size={16} />
          <span>К новым сообщениям</span>
        </button>
      )}
      <Typing />
      <Composer key={channel} />
      {dragging && (
        <div class="drop-hint">
          <Icon name="image" size={40} />
          <b>Отпустите, чтобы прикрепить</b>
          <span>PNG, JPEG, GIF, WebP — до 10 МБ</span>
        </div>
      )}
    </div>
  );
}

/** Whether message `m` goes under `before`, the one before it: the same writer, soon after, the same day,
 * and not an answer. */
function together(before: Message, m: Message): boolean {
  if (before.from !== m.from || m.reply !== null) return false;
  if (before.at === 0 || m.at === 0) return before.at === m.at;
  return m.at - before.at < TOGETHER && dayOf(before.at) === dayOf(m.at);
}

function dayOf(at: number): string {
  return at === 0 ? "" : new Date(at).toDateString();
}

/** Where another day begins. */
function DayLine({ at }: { at: number }) {
  return (
    <div class="day-line" role="separator">
      <span>{dayName(at)}</span>
    </div>
  );
}

function dayName(at: number): string {
  const d = new Date(at);
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86400000);
  if (d.toDateString() === today.toDateString()) return "Сегодня";
  if (d.toDateString() === yesterday.toDateString()) return "Вчера";
  const year = d.getFullYear() === today.getFullYear() ? undefined : "numeric";
  return d.toLocaleDateString("ru-RU", { day: "numeric", month: "long", year });
}

/** The time of a message: today's hour, yesterday's, or the day and the hour. */
function when(at: number): string {
  if (at === 0) return "";
  const d = new Date(at);
  const hm = d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  const name = dayName(at);
  if (name === "Сегодня") return `сегодня в ${hm}`;
  if (name === "Вчера") return `вчера в ${hm}`;
  return `${d.toLocaleDateString("ru-RU")} ${hm}`;
}

function hourOf(at: number): string {
  return at === 0 ? "" : new Date(at).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
}

function fullTime(at: number): string {
  return at === 0 ? "" : new Date(at).toLocaleString("ru-RU", { dateStyle: "full", timeStyle: "short" });
}

/** The name of a message's writer: as the meeting knows it now, else as it was then. */
function writer(m: { from: number; name: string }): string {
  return state.names.has(m.from) || m.from === state.user ? nameOf(m.from) : m.name;
}

/** The name's look by the writer's role: the host's, a moderator's, or anyone's. */
function roleClass(id: number): string {
  if (state.meeting !== null && state.meeting.host === id) return "author host";
  return isModerator(id) ? "author moderator" : "author";
}

function MessageView({ m, head, flash }: { m: Message; head: boolean; flash: boolean }) {
  const [picking, setPicking] = useState(false);
  const own = m.from === state.user;
  const classes = [
    "msg-row",
    head ? "head" : "cont",
    mentions(m.text).includes(state.user) && "mentioned",
    flash && "flash",
    state.selected === m.seq && "selected",
    picking && "picking",
  ];
  const touch = () => {
    if (!matchMedia("(pointer: coarse)").matches) return;
    state.selected = state.selected === m.seq ? 0 : m.seq;
    changed();
  };
  return (
    <div class={classes.filter((x) => x).join(" ")} data-seq={m.seq} onClick={touch}>
      {m.reply !== null && <Quoted q={m.reply} />}
      <div class="msg-gutter">
        {head ? (
          <Avatar id={m.from} size={40} />
        ) : (
          <time class="msg-hour" title={fullTime(m.at)}>
            {hourOf(m.at)}
          </time>
        )}
      </div>
      <div class="msg-main">
        {head && (
          <div class="msg-head">
            <span class={roleClass(m.from)}>{writer(m)}</span>
            {state.meeting?.host === m.from && (
              <span class="role-mark" title="Владелец">
                <Icon name="crown" size={14} />
              </span>
            )}
            {isModerator(m.from) && (
              <span class="role-mark" title="Модератор">
                <Icon name="shield" size={14} />
              </span>
            )}
            <time title={fullTime(m.at)}>{when(m.at)}</time>
          </div>
        )}
        {state.editing === m.seq ? (
          <EditBox m={m} />
        ) : (
          m.text.trim() !== "" && (
            <div class="msg-text">
              <Rich text={m.text} />
              {m.edited > 0 && (
                <span class="edited" title={`изменено ${fullTime(m.edited)}`}>
                  (изменено)
                </span>
              )}
            </div>
          )
        )}
        {m.files.length > 0 && <Gallery files={m.files} />}
        {m.reactions.length > 0 && <Reactions m={m} onMore={() => setPicking(true)} />}
      </div>
      {state.editing !== m.seq && (
        <div class="msg-acts" onClick={(e) => e.stopPropagation()}>
          {QUICK.map((e) => (
            <button key={e} class="quick" title={`Реакция ${e}`} onClick={() => react(m, e)}>
              {e}
            </button>
          ))}
          <IconButton icon="smilePlus" title="Добавить реакцию" onClick={() => setPicking(!picking)} />
          <IconButton
            icon="reply"
            title="Ответить"
            onClick={() => {
              answer(m);
              focusBox();
            }}
          />
          {own && <IconButton icon="pencil" title="Изменить" onClick={() => editing(m.seq)} />}
          {mayDelete(m) && (
            <IconButton
              icon="trash"
              class="danger"
              title="Удалить (с Shift — сразу)"
              onClick={(e) => remove(m, e.shiftKey)}
            />
          )}
        </div>
      )}
      {picking && (
        <div class="msg-picker" onClick={(e) => e.stopPropagation()}>
          <EmojiPicker place="below" onPick={(e) => react(m, e)} onClose={() => setPicking(false)} />
        </div>
      )}
    </div>
  );
}

/** The box of the chat, to write in at once (after "answer"). */
function focusBox(): void {
  document.querySelector<HTMLTextAreaElement>(".composer textarea")?.focus();
}

/** What a message answers: its writer and the start of its text — a click goes to it. Of one deleted
 * since nothing is left to show or go to: the server sends only its number, and a channel read whole has
 * it no more. */
function Quoted({ q }: { q: Quote }) {
  if (quotesDeleted(q) || (!state.messages.has(q.seq) && state.all))
    return (
      <div class="quoted gone">
        <span class="quoted-spine" />
        <span class="quoted-text">
          <i>сообщение удалено</i>
        </span>
      </div>
    );
  return (
    <button
      class="quoted"
      title="Перейти к сообщению"
      onClick={(e) => {
        e.stopPropagation();
        jumpTo(q.seq);
      }}
    >
      <span class="quoted-spine" />
      <Avatar id={q.from} size={16} />
      <span class={roleClass(q.from)}>{writer(q)}</span>
      <span class="quoted-text">
        {q.text.trim() !== "" ? (
          <RichLine text={q.text} />
        ) : (
          <i>
            <Icon name="image" size={14} /> изображение
          </i>
        )}
      </span>
    </button>
  );
}

/** The user edits message `m` in its place: Enter keeps the text, Escape leaves it as it was. */
function EditBox({ m }: { m: Message }) {
  const [first] = useState(() => boxed(m.text));
  const field = useRef<HTMLTextAreaElement>(null);
  const grow = (f: HTMLTextAreaElement) => {
    f.style.height = "auto";
    f.style.height = `${Math.min(300, f.scrollHeight)}px`;
  };
  useLayoutEffect(() => {
    const f = field.current!;
    f.focus();
    f.setSelectionRange(f.value.length, f.value.length);
    grow(f);
  }, []);
  return (
    <div class="edit-box" onClick={(e) => e.stopPropagation()}>
      <textarea
        ref={field}
        defaultValue={first[0]}
        onInput={(e) => grow(e.currentTarget)}
        onKeyDown={(e) => {
          if (e.key === "Escape") editing(0);
          else if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
            e.preventDefault();
            saveEdit(m, e.currentTarget.value, first[1]);
          }
        }}
      />
      <small>
        Escape — <button onClick={() => editing(0)}>отмена</button> · Enter —{" "}
        <button onClick={() => saveEdit(m, field.current!.value, first[1])}>сохранить</button>
      </small>
    </div>
  );
}

/** The images of a message: one as large as it fits, several in a grid; a click shows one whole. */
function Gallery({ files }: { files: FileRef[] }) {
  const open = (e: MouseEvent, i: number) => {
    e.stopPropagation();
    state.lightbox = { files, index: i };
    changed();
  };
  if (files.length === 1) {
    const f = files[0];
    const k = Math.min(1, IMAGE_W / f.width, IMAGE_H / f.height);
    const [w, h] = [Math.max(40, Math.round(f.width * k)), Math.max(40, Math.round(f.height * k))];
    return (
      <div class="gallery one">
        <button class="image" style={`width:${w}px;aspect-ratio:${f.width}/${f.height}`} onClick={(e) => open(e, 0)}>
          <img src={fileUrl(f.preview || f.id)} alt={f.name} width={w} height={h} loading="lazy" />
          {f.id.endsWith(".gif") && <span class="gif">GIF</span>}
        </button>
      </div>
    );
  }
  return (
    <div class={`gallery many n${Math.min(files.length, 4)}`}>
      {files.map((f, i) => (
        <button key={f.id} class="image" onClick={(e) => open(e, i)}>
          <img src={fileUrl(f.preview || f.id)} alt={f.name} loading="lazy" />
          {f.id.endsWith(".gif") && <span class="gif">GIF</span>}
        </button>
      ))}
    </div>
  );
}

/** The reactions to a message: each emoji, how many — lit where the user's is among them — and who, under
 * the pointer; a click gives the user's or takes it back. */
function Reactions({ m, onMore }: { m: Message; onMore: () => void }) {
  return (
    <div class="reactions" onClick={(e) => e.stopPropagation()}>
      {m.reactions.map((r) => {
        const mine = r.users.includes(state.user);
        const more = r.users.length > 6 ? ` и ещё ${r.users.length - 6}` : "";
        return (
          <button
            key={r.emoji}
            class={mine ? "reaction own" : "reaction"}
            title={`${r.users.slice(0, 6).map(nameOf).join(", ")}${more}: ${r.emoji}`}
            aria-pressed={mine}
            onClick={() => react(m, r.emoji)}
          >
            <span class="reaction-emoji">{r.emoji}</span>
            <span class="reaction-count">{r.users.length}</span>
          </button>
        );
      })}
      <button class="reaction add" title="Добавить реакцию" onClick={onMore}>
        <Icon name="smilePlus" size={16} />
      </button>
    </div>
  );
}

/** The user's message the server has not confirmed: paler, its images as they go up. */
function PendingView({ p }: { p: Pending }) {
  const r = p.reply;
  return (
    <div class="msg-row head pending">
      {r !== null && <Quoted q={{ seq: r.seq, from: r.from, name: r.name, text: r.text, files: r.files.length }} />}
      <div class="msg-gutter">
        <Avatar id={state.user} size={40} />
      </div>
      <div class="msg-main">
        <div class="msg-head">
          <span class="author">{nameOf(state.user)}</span>
          <time>{p.waiting && p.files.length > 0 ? "загружаю изображения…" : "отправляю…"}</time>
        </div>
        {p.text.trim() !== "" && (
          <div class="msg-text">
            <Rich text={p.text} />
          </div>
        )}
        {p.files.length > 0 && (
          <div class={`gallery many n${Math.min(p.files.length, 4)}`}>
            {p.files.map((a) => (
              <span key={a.key} class="image">
                {a.url !== "" && <img src={a.url} alt={a.name} />}
                {a.status !== "done" && (
                  <span class="attachment-progress">
                    <span style={`width:${Math.round(Math.max(0.05, a.progress) * 100)}%`} />
                  </span>
                )}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Who types, in words, with dots that move. */
function Typing() {
  const who = state.typists.map(nameOf);
  const text =
    who.length === 0
      ? ""
      : who.length === 1
        ? `${who[0]} печатает`
        : who.length <= 3
          ? `${who.slice(0, -1).join(", ")} и ${who[who.length - 1]} печатают`
          : "Несколько человек печатают";
  return (
    <div class="typing" aria-live="polite">
      {text !== "" && (
        <>
          <span class="dots">
            <i />
            <i />
            <i />
          </span>
          <span>{text}…</span>
        </>
      )}
    </div>
  );
}
